import { createColumnHelper } from '@tanstack/react-table';

import type { LocationInventoryDTO } from '@invenco/common-interface/inventory';

import { Card, CardHeader } from '@/components/core/card';
import {
  DataTable,
  DataTableCardBody,
  DataTableCardTitle,
  useDataTable,
  useDataTableState,
} from '@/components/data-table';
import { StatusBadge } from '@/components/status-badge';
import { useGateways } from '@/gateways/GatewayProvider';
import { formatNumber } from '@/shared/helpers';
import { useQueryWithInput } from '@/shared/hooks/queries';
import { locationInventoryStatusType } from '@/shared/status-maps';
import { locationInventoryStatusTitle } from '@/shared/title-maps';

type Props = {
  skuId?: string;
};

const columnHelper = createColumnHelper<LocationInventoryDTO>();

const columns = [
  columnHelper.accessor('locationName', {
    header: 'Location',
  }),
  columnHelper.accessor('status', {
    header: 'Status',
    cell: ({ getValue }) => (
      <StatusBadge
        status={getValue()}
        type={locationInventoryStatusType}
        title={locationInventoryStatusTitle}
      />
    ),
  }),
  columnHelper.accessor('qtyExpected', {
    header: 'Expected',
    cell: ({ getValue }) => formatNumber(getValue()),
    meta: { align: 'right' },
  }),
  columnHelper.accessor('qtyOnHand', {
    header: 'On hand',
    cell: ({ getValue }) => formatNumber(getValue()),
    meta: { align: 'right' },
  }),
  columnHelper.accessor('qtyReserved', {
    header: 'Reserved',
    cell: ({ getValue }) => formatNumber(getValue()),
    meta: { align: 'right' },
  }),
  columnHelper.accessor('qtyAvailable', {
    header: 'Available',
    cell: ({ getValue }) => formatNumber(getValue()),
    meta: { align: 'right' },
  }),
];

export function InventoryTable({ skuId }: Props) {
  const { inventoryGateway } = useGateways();
  const { data, isLoading, isError } = useQueryWithInput({
    parentKey: 'inventories',
    input: { skuIds: [skuId!] },
    query: (input, { signal }) => inventoryGateway.getInventories(input, { signal }),
    enabled: Boolean(skuId),
  });

  const { state, onSortingChange } = useDataTableState({
    columns,
    tableId: 'sku-details-inventory',
    defaultPageSize: 10,
  });

  const table = useDataTable({
    data: data?.items ?? [],
    columns,
    isLoading,
    hasError: isError,
    manualPagination: true,
    manualSorting: false,
    state,
    onSortingChange,
  });

  return (
    <Card aria-label="Inventory">
      <CardHeader>
        <DataTableCardTitle table={table}>Inventory</DataTableCardTitle>
      </CardHeader>
      <DataTableCardBody table={table}>
        <DataTable table={table} />
      </DataTableCardBody>
    </Card>
  );
}
